import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Bot, Play, Square, Trash2, Settings as SettingsIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import MyAssets from '../components/MyAssets';
import { useLanguage } from '../lib/i18n';
import { startTrading, stopTrading, removeBot } from '../lib/trading';
import UserTradingSettings from '../components/UserTradingSettings';

type TradingBot = {
  id: string;
  name: string;
  description: string;
  risk_level: 'low' | 'medium' | 'high';
  pnl_percentage: number;
  running_days: number;
  webhook_url: string;
  is_active: boolean;
  created_at: string;
};

function Dashboard() {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [preparedBots, setPreparedBots] = useState<TradingBot[]>([]);
  const [activeBots, setActiveBots] = useState<TradingBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [botStatuses, setBotStatuses] = useState<Record<string, any>>({});
  const [loadingStatuses, setLoadingStatuses] = useState(true);
  const [showSettings, setShowSettings] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      loadBots();
      loadBotStatuses();
      
      // Subscribe to bot status changes
      const statusSubscription = supabase
        .channel('bot_status_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_bot_status'
          },
          () => {
            loadBotStatuses();
          }
        )
        .subscribe();

      return () => {
        statusSubscription.unsubscribe();
      };
    }
  }, [user]);

  const checkAuth = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      
      if (!session?.user) {
        navigate('/login', { replace: true });
        return;
      }

      setUser(session.user);
      
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      
      if (profileError) throw profileError;
      setProfile(profileData);

      // If user is admin, redirect to admin dashboard
      if (profileData.is_admin) {
        navigate('/admin', { replace: true });
        return;
      }
    } catch (error: any) {
      console.error('Error loading user data:', error);
      toast.error('Failed to load user data');
      navigate('/login', { replace: true });
    }
  };

  const loadBots = async () => {
    if (!user?.id) return;

    try {
      // Get selected bots and their trading status
      const { data: selections, error: selectionsError } = await supabase
        .from('user_selections')
        .select(`
          bot_id,
          user_bot_status!inner (
            is_trading
          ),
          trading_bots!inner (*)
        `)
        .eq('user_id', user.id)
        .eq('is_selected', true);

      if (selectionsError) throw selectionsError;

      // Separate bots into prepared and active
      const prepared: TradingBot[] = [];
      const active: TradingBot[] = [];

      selections?.forEach(selection => {
        if (selection.user_bot_status[0]?.is_trading) {
          active.push(selection.trading_bots);
        } else {
          prepared.push(selection.trading_bots);
        }
      });

      setPreparedBots(prepared);
      setActiveBots(active);
    } catch (error: any) {
      console.error('Error loading bots:', error);
      toast.error('Failed to load your bots');
    } finally {
      setLoading(false);
    }
  };

  const loadBotStatuses = async () => {
    if (!user?.id) return;

    try {
      const { data: statuses, error } = await supabase
        .from('user_bot_status')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      // Convert array to object for easier lookup
      const statusMap = (statuses || []).reduce((acc, status) => ({
        ...acc,
        [status.bot_id]: status
      }), {});

      setBotStatuses(statusMap);
    } catch (error) {
      console.error('Error loading bot statuses:', error);
    } finally {
      setLoadingStatuses(false);
    }
  };

  const handleStartTrading = async (bot: TradingBot) => {
    if (!user?.id) return;

    try {
      await startTrading(user.id, bot.id);
      // Move bot from prepared to active
      setPreparedBots(prev => prev.filter(b => b.id !== bot.id));
      setActiveBots(prev => [...prev, bot]);
      toast.success('Bot started trading successfully');
    } catch (error: any) {
      console.error('Error starting bot:', error);
      toast.error('Failed to start bot trading');
    }
  };

  const handleStopTrading = async (bot: TradingBot) => {
    if (!user?.id) return;

    try {
      await stopTrading(user.id, bot.id);
      // Move bot from active to prepared
      setActiveBots(prev => prev.filter(b => b.id !== bot.id));
      setPreparedBots(prev => [...prev, bot]);
      toast.success('Bot stopped trading successfully');
    } catch (error: any) {
      console.error('Error stopping bot:', error);
      toast.error('Failed to stop bot trading');
    }
  };

  const handleRemoveBot = async (botId: string) => {
    if (!user?.id) return;

    if (!confirm('Are you sure you want to remove this bot? However, you can reselect in Trading Bots.')) {
      return;
    }

    try {
      await removeBot(user.id, botId);
      setPreparedBots(prev => prev.filter(bot => bot.id !== botId));
      setActiveBots(prev => prev.filter(bot => bot.id !== botId));
      toast.success('Bot removed successfully');
    } catch (error: any) {
      console.error('Error removing bot:', error);
      toast.error('Failed to remove bot');
    }
  };

  const handleSaveSettings = async (botId: string, settings: any) => {
    if (!user?.id) return;

    try {
      // Get current bot status first
      const { data: currentStatus } = await supabase
        .from('user_bot_status')
        .select('is_trading')
        .eq('user_id', user.id)
        .eq('bot_id', botId)
        .single();

      await startTrading(user.id, botId, {
        ...settings,
        is_trading: currentStatus?.is_trading || false // Preserve trading state
      });
      
      setShowSettings(null);
      toast.success('Settings saved successfully');
      await loadBots(); // Reload bots to reflect changes
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  const renderBotCard = (bot: TradingBot, isActive: boolean) => (
    <div key={`${bot.id}-${isActive ? 'active' : 'prepared'}`} className="space-y-4">
      <div
        className={`bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border ${
          isActive ? 'border-green-500/20' : 'border-blue-500/20'
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white">{bot.name}</h3>
            <p className="text-gray-300">{bot.description}</p>
          </div>
          <div className="flex items-center space-x-2">
            {isActive ? (
              <button
                onClick={() => handleStopTrading(bot)}
                className="p-2 text-red-400 hover:text-red-300 transition-colors"
                title="Stop Trading"
              >
                <Square className="h-5 w-5" />
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleStartTrading(bot)}
                  className="p-2 text-green-400 hover:text-green-300 transition-colors"
                  title="Start Trading"
                >
                  <Play className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleRemoveBot(bot.id)}
                  className="p-2 text-red-400 hover:text-red-300 transition-colors"
                  title="Remove Bot"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </>
            )}
            <button
              onClick={() => setShowSettings(showSettings === bot.id ? null : bot.id)}
              className="p-2 text-cyan-400 hover:text-cyan-300 transition-colors"
              title="Trading Settings"
            >
              <SettingsIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {isActive && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="bg-black/20 rounded-lg p-3">
              <p className="text-sm text-gray-400">PnL</p>
              <p className={`text-2xl font-bold ${
                botStatuses[bot.id]?.pnl >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {botStatuses[bot.id]?.pnl >= 0 ? '+' : ''}{botStatuses[bot.id]?.pnl || 0}%
              </p>
            </div>
            <div className="bg-black/20 rounded-lg p-3">
              <p className="text-sm text-gray-400">Last Trade</p>
              <p className="text-sm font-light text-cyan-400">
                {botStatuses[bot.id]?.last_trade_at ? 
                  new Date(botStatuses[bot.id].last_trade_at).toLocaleString() : 
                  'No trades yet'}
              </p>
            </div>
            <div className="bg-black/20 rounded-lg p-3">
              <p className="text-sm text-gray-400">Running Days</p>
              <p className="text-sm font-light text-cyan-400">{bot.running_days}d</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3">
              <p className="text-sm text-gray-400">Status</p>
              {botStatuses[bot.id]?.error_count > 0 ? (
                <div className="text-yellow-400">
                  <p className="text-sm font-medium">Warning</p>
                  <p className="text-xs font-light">{botStatuses[bot.id].last_error}</p>
                </div>
              ) : (
                <p className="text-sm font-light text-green-400">Active</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Trading Settings */}
      {showSettings === bot.id && (
        <UserTradingSettings
          key={`settings-${bot.id}`}
          botId={bot.id}
          onClose={() => setShowSettings(null)}
          onSave={(settings) => handleSaveSettings(bot.id, settings)}
        />
      )}
    </div>
  );

  if (loading || loadingStatuses) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* My Assets */}
      <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
        <h2 className="text-xl font-semibold text-white mb-6">{t('dashboard.myAssets')}</h2>
        <MyAssets />
      </div>

      {/* Active Trading Bots */}
      {activeBots.length > 0 && (
        <div key="active-bots-section">
          <h3 className="text-xl font-semibold text-white mb-4">Active Trading Bots</h3>
          <div className="grid grid-cols-1 gap-6">
            {activeBots.map(bot => renderBotCard(bot, true))}
          </div>
        </div>
      )}

      {/* Prepared Bots */}
      {preparedBots.length > 0 && (
        <div key="prepared-bots-section">
          <h3 className="text-xl font-semibold text-white mb-4">Prepared Bots</h3>
          <div className="grid grid-cols-1 gap-6">
            {preparedBots.map(bot => renderBotCard(bot, false))}
          </div>
        </div>
      )}

      {preparedBots.length === 0 && activeBots.length === 0 && (
        <div key="no-bots-section" className="text-center py-12">
          <h3 className="text-xl font-medium text-white mb-2">No Robots Selected</h3>
          <p className="text-gray-300 mb-8">Start by selecting a trading bot from our collection.</p>
          <Link
            to="/trading-bots"
            className="inline-flex items-center px-6 py-3 border border-cyan-500 text-cyan-500 hover:bg-cyan-500/10 rounded-md transition-colors"
          >
            <Bot className="h-5 w-5 mr-2" />
            Browse Trading Bots
          </Link>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
